import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { auth } from "../../config";
import Loader from "../../components/Loader";
import "../Home/Home.css";
import "../forgetPassword/forgetPassword.css";
import "./Password.css";

function ChangePasswordForm() {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!success) return undefined;

    const timer = setTimeout(() => {
      navigate("/dashboard");
    }, 2500);

    return () => clearTimeout(timer);
  }, [success, navigate]);

  const getAuthErrorMessage = (err) => {
    switch (err?.code) {
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "Current password is incorrect.";
      case "auth/weak-password":
        return "New password is too weak. Use at least 6 characters.";
      case "auth/requires-recent-login":
        return "Please sign out and sign in again, then try changing your password.";
      default:
        return err?.message || "Could not update your password. Please try again.";
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setError("");
    setProcessing(true);

    const user = auth.currentUser;
    if (!user?.email) {
      setError("You must be signed in to change your password.");
      setProcessing(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      setProcessing(false);
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      setProcessing(false);
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccess(true);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error(err);
      setError(getAuthErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="auth-page auth-page--centered change-password-page">
      <div className="auth-panel">
        <div className="auth-card">
          <Link to="/dashboard" className="auth-back-link">
            ← Back to dashboard
          </Link>

          <form onSubmit={handleChangePassword} className="auth-form">
            <h2 className="auth-form__title">Change password</h2>
            <p className="auth-form__subtitle">
              Enter your current password, then choose a new one.
            </p>

            {success ? (
              <div className="auth-form__success">
                <p>Your password has been updated.</p>
                <button
                  type="button"
                  className="auth-form__submit"
                  onClick={() => navigate("/dashboard")}
                >
                  Return to dashboard
                </button>
                <p className="change-password-page__redirect-hint">
                  Redirecting automatically…
                </p>
              </div>
            ) : (
              <>
                <div className="auth-form__field">
                  <label htmlFor="old-password">Current password</label>
                  <input
                    id="old-password"
                    type="password"
                    placeholder="Current password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>

                <div className="auth-form__field">
                  <label htmlFor="new-password">New password</label>
                  <input
                    id="new-password"
                    type="password"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div className="auth-form__field">
                  <label htmlFor="confirm-password">Confirm new password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>

                {error && (
                  <p className="auth-form__error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="auth-form__submit"
                  disabled={processing}
                >
                  {processing ? <Loader size={22} /> : "Update password"}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordForm;
